import { Breakpoints, MQ } from 'style/styled-components/cssMediaQueries';
import React, { useEffect, useRef, useState } from 'react';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { css } from 'styled-components';
import { isIOS } from 'react-device-detect';
import icons from 'style';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import mainImages from 'assets/images/main';
import {
    body1FontSize,
    body1LineHeight,
    body2FontSize,
    body2LineHeight,
    body3FontSize,
    body3LineHeight,
    fontBold,
} from 'style/styled-components/cssGlobalVariables';
import { useApp, usePageUtils } from 'providers/useApp/AppContext';
import translate from 'utils/fnTranslate';
import { useNavigation } from 'hooks/useNavigation';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import { usePageScroller } from 'components/PageScroller/usePageScroller';
import { useSearch } from 'providers/useSearch/SearchContext';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { LinkTrackable } from 'components/Trackable/LinkTrackable';
import { getLocalOrRemoteImagePath } from 'utils/fnData';
import { HeaderSearch } from './HeaderSearch/HeaderSearch';
import { DropdownMenuSettings } from './DropdownMenuSettings';
import { MainMenu } from './MainMenu';
import {
    GlobalBackNavigation,
    GlobalNavPageTitle,
    HeaderButtonsWrapper,
    HeaderCountWrapper,
    HeaderWrapper,
    Logo,
    StyledSVGInline,
} from './Header.css';

const pageTitleParser = (title: string) => {
    // separates content between ()
    const regExp = /\(([^)]+)\)/;

    const newTitle = title?.replace(regExp, '');
    const count = regExp?.exec(title)?.[1];

    return { newTitle, count };
};

export const Header: React.FC = ({ ...rest }) => {
    const [isSticky, setIsSticky] = useState<boolean>(false);
    const { pageTitle, isDesktop } = useApp();
    const { onBackPressed, navigate } = useNavigation();
    const { isDetailPage } = useDetailLocationParams();
    const { scrollTop } = usePageScroller();
    const { pageBelongsToMainSections, isCollectionPage, isSettings, isSearch, hasBackNavigation, hasSubpageBackNavigation } =
        usePageUtils();
    const { searchTerm, search, exitSearch, enterSearch } = useSearch();
    const { config } = useConfig();
    const { newTitle: title, count } = pageTitleParser(pageTitle);

    const headerRef = useRef(null);

    const handleLogoClick = () => {
        navigate('/');
        exitSearch(false);
    };

    const onSearch = (value: string) => {
        if (!isSearch) {
            enterSearch(value);
            return;
        }

        if (value?.length === 0 || value?.length >= config.app_config.search_settings.key_threshold) {
            search(value);
        }
    };

    const closeSearch = () => {
        if (isSearch) {
            exitSearch(true);
        }
    };

    const submitSearch = (value: string) => {
        if (!isSearch) {
            enterSearch(value);
            return;
        }

        search(value);
    };

    const openSearchScreen = () => {
        // Currently not needed, but let is sit here for a while... :D
    };

    const searchInput = () =>
        config?.isFeatureActive('search') && (
            <HeaderSearch
                value={searchTerm}
                placeholder={translate('SEARCH_PLACEHOLDER_TEXT')}
                isSearchPage={isSearch}
                isOpen={isSearch}
                onSearch={onSearch}
                onSubmit={submitSearch}
                onClose={closeSearch}
                onOpen={openSearchScreen}
            />
        );

    useEffect(() => {
        setIsSticky(scrollTop > 0);
    }, [scrollTop]);

    useKeyboardShortcuts({
        backspace: hasBackNavigation ? onBackPressed : undefined,
    });

    return (
        <HeaderWrapper
            ref={headerRef}
            isSticky={isSticky || isCollectionPage || !pageBelongsToMainSections}
            hideOnMobile={isDetailPage}
            hasSubpageBackNavigation={hasSubpageBackNavigation}
            className={hasBackNavigation ? 'with-back-navigation' : ''}
            {...rest}
        >
            <Logo
                href="/"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    handleLogoClick();
                }}
                aria-label={translate('MODULE_MAIN_MENU_HOME')}
                imageSrc={getLocalOrRemoteImagePath(config?.layout_config?.logo, mainImages)}
                data-test-id={'header-logo'}
            />
            <div className="menu-wrapper">
                <MainMenu id={'MainMenu'} isIOS={isIOS} />
                <HeaderButtonsWrapper hideOnMobile={isDetailPage}>
                    {isSettings ? (isDesktop() ? searchInput() : null) : searchInput()}
                    {isSettings ? isDesktop() ? <DropdownMenuSettings /> : null : <DropdownMenuSettings />}
                </HeaderButtonsWrapper>
            </div>
            {hasBackNavigation && (
                <GlobalBackNavigation type="button" onClick={() => onBackPressed()}>
                    <StyledSVGInline data-test-id={'header-account-backbutton'} svg={icons.leftArrowIcon} />
                    <GlobalNavPageTitle data-test-id={'header-account-sectionbutton'}>{translate(title)}</GlobalNavPageTitle>
                    <HeaderCountWrapper>{count}</HeaderCountWrapper>
                </GlobalBackNavigation>
            )}
        </HeaderWrapper>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const MenuLink = styled(({ to, active, ...rest }) => <LinkTrackable to={to} {...rest} />)<{ active?: boolean; to: string }>`
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    display: inline-block;
    font-size: ${body3FontSize};
    line-height: ${body3LineHeight};
    opacity: 0.66;
    font-family: ${fontBold};

    ${MQ(Breakpoints.s)} {
        font-size: ${body2FontSize};
        line-height: ${body2LineHeight};
    }

    ${MQ(Breakpoints.m)} {
        font-size: ${body1FontSize};
        line-height: ${body1LineHeight};
    }

    &:hover {
        opacity: 1 !important;
    }

    &:focus-visible {
        border-bottom: var(--focus-outline-solid);
        transition: border 0s;
    }

    ${(props) =>
        props.active
            ? css`
                  opacity: 1;
              `
            : ``}
`;
